import { escapeHtml, fromNow, stamp } from '../inventoree/format'
import type { Column } from '../inventoree/columns'
import { gaugeHtml } from './liveBatch'

const DASH = '<i class="fas fa-minus font-10"></i>'

export interface DeviceRow {
  deviceId: number
  DT_RowId: string
  Tip: number
  device_type: string
  device_icon: string
  actions?: string
  Denumire: string
  ownerId: number
  Prenume: string | null
  Nume: string | null
  UserLogat: string | null
  office: string | null
  CriticalAlarms?: number | string | null
  MajorAlarms?: number | string | null
  uptime_seconds?: number | string | null
  cpu_usage?: number | string | null
  ram_usage?: number | string | null
  swap_usage?: number | string | null
  net_connections?: number | string | null
  running_applications?: number | string | null
  commands_pending?: number | string | null
  core_application?: { name?: string; by?: string } | null
  server_role?: { label?: string; matched?: string } | null
  server_service?: {
    status?: string
    role?: { label?: string; matched?: string }
    health?: { summary?: string }
    reasons?: { text: string }[]
    next_action?: { text?: string }
    kpi?: { label?: string; display?: string; severity?: string }
    upgrade?: { current?: string; available?: string; reason?: string }
  } | null
  stopped_services?: { stopped?: number; names?: string[]; outages_30d?: number } | null
  open_ports?: { port?: string | number }[] | null
  last_login?: { user?: string; at?: string; online?: boolean; open?: boolean; open_count?: number } | null
  __checked?: boolean
  [key: string]: unknown
}

function textOrDash(value: unknown): string {
  return value ? escapeHtml(value) : DASH
}

function nameCell(row: DeviceRow): string {
  const name = `<p>${escapeHtml(row.Denumire)}</p>`
  if (row.ownerId > 0) {
    return name + `<span class="color-light-grey"><i class="fa fa-user"></i> ${escapeHtml(row.Prenume)} ${escapeHtml(row.Nume)}</span>`
  }
  return name + '<span class="color-red"><i class="fa fa-user"></i> Unassigned</span>'
}

/** Convert.FormatSecondsAsShortDuration: "4d 2h", "3h 12m", "45s". */
export function formatSecondsAsShortDuration(seconds: unknown): string {
  const n = parseInt(String(seconds), 10)
  if (!n || n < 0) return '0s'
  const units = [
    { suffix: 'd', size: 86400 },
    { suffix: 'h', size: 3600 },
    { suffix: 'm', size: 60 },
    { suffix: 's', size: 1 },
  ]
  for (let i = 0; i < units.length; i++) {
    const value = Math.floor(n / units[i].size)
    if (value <= 0) continue
    let out = value + units[i].suffix
    const rest = n - value * units[i].size
    if (i + 1 < units.length) {
      const second = Math.floor(rest / units[i + 1].size)
      if (second > 0) out += ' ' + second + units[i + 1].suffix
    }
    return out
  }
  return '0s'
}

function uptimeCell(seconds: unknown): string {
  const n = parseInt(String(seconds ?? ''), 10)
  if (n > 0) return formatSecondsAsShortDuration(n)
  return DASH
}

/** Discul desenat in SVG (liveBatch.ts); `data-od-live` e carligul pe care il retuseaza bataia la 2 s. */
function gauge(percent: unknown, kind: string): string {
  return gaugeHtml(percent, kind)
}

function alarmLabel(count: unknown, cls: string): string {
  return `<label class="label ${cls}"><i class="far fa-bell" aria-hidden="true"></i> ${escapeHtml(count ?? 0)}</label>`
}

function pendingCommands(row: DeviceRow): string {
  const n = parseInt(String(row.commands_pending ?? 0), 10) || 0
  if (n === 1) {
    return '<i data-toggle="tooltip" data-placement="top" title="1 command pending" class="fas fa-exchange-alt font-18 color-yellow"></i>'
  }
  if (n > 1) {
    return `<i data-toggle="tooltip" data-placement="top" title="${n} commands pending" class="fas fa-exchange-alt font-18 color-red"></i>`
  }
  return '<i data-toggle="tooltip" data-placement="top" title="No commands pending" class="fas fa-exchange-alt font-18"></i>'
}

function coreApp(row: DeviceRow): string {
  if (!row.core_application?.name) return DASH
  const title = row.core_application.by === 'cpu' ? 'Most CPU time' : 'Most time used'
  return `<span title="${title}">${escapeHtml(row.core_application.name)}</span>`
}

function expandCell(row: DeviceRow): string {
  return `<center class="show_details color-dark-grey pointer font-16" data-id="${row.deviceId}" data-type="${row.Tip}"><i class="fas fa-plus-circle"></i></center>`
}

function typeCell(row: DeviceRow): string {
  return `<center style="cursor:default" data-toggle="tooltip" data-placement="top" title="${escapeHtml(row.device_type)}">${row.device_icon}</center>`
}

/** ProactiviteeDevicesModel */
export function deviceColumns(): Column[] {
  return [
    { index: 0, title: '', orderable: false, visible: true, width: 50, html: (r) => expandCell(r as DeviceRow) },
    { index: 1, title: 'Type', name: 'Tip', orderable: true, visible: true, width: 100, html: (r) => typeCell(r as DeviceRow) },
    { index: 2, title: 'Device name', name: 'Denumire', orderable: true, visible: true, html: (r) => nameCell(r as DeviceRow) },
    { index: 3, title: 'Last User Logged On', name: 'UserLogat', orderable: true, visible: true, html: (r) => textOrDash((r as DeviceRow).UserLogat) },
    { index: 4, title: 'Office', name: 'SediuID', orderable: true, visible: true, html: (r) => textOrDash((r as DeviceRow).office) },
    { index: 5, title: 'Critical alarms', name: 'CriticalAlarms', orderable: true, visible: true, html: (r) => alarmLabel((r as DeviceRow).CriticalAlarms, 'label-danger') },
    { index: 6, title: 'Major alarms', name: 'MajorAlarms', orderable: true, visible: true, html: (r) => alarmLabel((r as DeviceRow).MajorAlarms, 'label-warning') },
    { index: 7, title: 'Uptime', name: 'uptime_seconds', orderable: true, visible: true, html: (r) => uptimeCell((r as DeviceRow).uptime_seconds) },
    { index: 8, title: 'CPU usage', name: 'cpu_usage', orderable: true, visible: true, html: (r) => ((r as DeviceRow).cpu_usage ? gauge((r as DeviceRow).cpu_usage, 'cpu') : DASH) },
    { index: 9, title: 'Memory usage', name: 'ram_usage', orderable: true, visible: true, html: (r) => ((r as DeviceRow).ram_usage ? gauge((r as DeviceRow).ram_usage, 'ram') : DASH) },
    { index: 10, title: 'Swap usage', name: 'swap_usage', orderable: true, visible: true, html: (r) => {
      const v = (r as DeviceRow).swap_usage
      return v !== null && v !== '' && typeof v !== 'undefined' ? gauge(v, 'swap') : DASH
    } },
    { index: 11, title: 'Network connections', name: 'net_connections', orderable: true, visible: true, html: (r) => {
      const n = parseInt(String((r as DeviceRow).net_connections ?? 0), 10)
      return n > 0 ? `<span class="label label-info" data-od-live="net">${escapeHtml(n)}</span>` : DASH
    } },
    { index: 12, title: 'Running applications', name: 'running_applications', orderable: true, visible: true, html: (r) => ((r as DeviceRow).running_applications ? `<span class="label label-success">${escapeHtml((r as DeviceRow).running_applications)}</span>` : DASH) },
    { index: 13, title: 'Core Application', orderable: false, visible: true, html: (r) => coreApp(r as DeviceRow) },
    { index: 14, title: 'Actions', orderable: false, visible: true, width: 115, html: (r) => ((r as DeviceRow).actions ? String((r as DeviceRow).actions) : DASH) },
    { index: 15, title: 'Pending Commands', name: 'commands_pending', orderable: true, visible: false, width: 100, html: (r) => pendingCommands(r as DeviceRow) },
  ]
}

function statusBadge(service: DeviceRow['server_service']): string {
  if (!service?.status) return DASH
  const map: Record<string, { cls: string; text: string }> = {
    healthy: { cls: 'label-success', text: 'Healthy' },
    degraded: { cls: 'label-warning', text: 'Degraded' },
    down: { cls: 'label-danger', text: 'Down' },
    unknown: { cls: 'label-default', text: 'Unknown' },
  }
  const m = map[service.status] ?? map.unknown
  let tip = service.health?.summary ?? service.status
  if (service.reasons?.length) tip = service.reasons.map((x) => x.text).join('; ') + (tip ? ' — ' + tip : '')
  if (service.next_action?.text) tip = service.next_action.text + (tip ? ' — ' + tip : '')
  return `<span class="label ${m.cls}" data-toggle="tooltip" data-placement="top" title="${escapeHtml(tip)}">${m.text}</span>`
}

function focusBadge(row: DeviceRow): string {
  const kpi = row.server_service?.kpi
  if (!kpi) return DASH
  const cls = kpi.severity === 'danger' ? 'label-danger' : kpi.severity === 'warning' ? 'label-warning' : 'label-info'
  let tip = `${kpi.label ?? ''} ${kpi.display ?? ''}`
  const upgrade = row.server_service?.upgrade
  if (upgrade?.current) {
    tip += ` — version ${upgrade.current}${upgrade.available ? ' → ' + upgrade.available : ''}${upgrade.reason ? ' (' + upgrade.reason + ')' : ''}`
  }
  if (row.server_service?.reasons?.length) tip += ' — ' + row.server_service.reasons.map((x) => x.text).join('; ')
  if (row.server_service?.next_action?.text) tip += ' — ' + row.server_service.next_action.text
  const text = `${kpi.label ?? ''} ${kpi.display ?? ''}`
  return `<span class="label ${cls}" data-toggle="tooltip" data-placement="top" title="${escapeHtml(tip)}">${escapeHtml(text)}</span>`
}

function servicesCell(row: DeviceRow): string {
  const services = row.stopped_services
  if (services && (services.stopped ?? 0) > 0) {
    const names = (services.names ?? []).join(', ')
    const more = (services.stopped ?? 0) > (services.names ?? []).length ? ' and more' : ''
    const repeats = (services.outages_30d ?? 0) > 1 ? `\n${services.outages_30d} service outages in the last 30 days` : ''
    return `<span class="label label-danger" data-toggle="tooltip" data-placement="top" title="${escapeHtml(names + more + repeats)}"><i class="fas fa-exclamation-triangle"></i> ${services.stopped} stopped</span>`
  }
  if (row.running_applications && parseInt(String(row.running_applications), 10) > 0) {
    return '<i class="fas fa-check-circle font-20 color-green2" data-toggle="tooltip" data-placement="top" title="No automatic service is stopped"></i>'
  }
  return DASH
}

function lastLoginCell(row: DeviceRow): string {
  const login = row.last_login
  if (!login?.user) {
    if (row.UserLogat) {
      return `<span class="color-light-grey" data-toggle="tooltip" data-placement="top" title="From the inventory - no login time recorded yet">${escapeHtml(row.UserLogat)}</span>`
    }
    return DASH
  }
  let mark = ''
  if (login.online) {
    const open = (login.open_count ?? 0) > 1 ? `${login.open_count} sessions open` : 'Still logged in'
    mark = ` <i class="fas fa-circle color-green2 font-10" data-toggle="tooltip" data-placement="top" title="${escapeHtml(open)}"></i>`
  } else if (login.open) {
    mark = ' <i class="fas fa-circle color-light-grey font-10" data-toggle="tooltip" data-placement="top" title="Was logged in - the machine has not reported since"></i>'
  }
  return `<p style="margin:0">${escapeHtml(login.user)}${mark}</p><span class="color-light-grey font-11">${escapeHtml(login.at)}</span>`
}

function openPortsCell(row: DeviceRow): string {
  const ports = row.open_ports
  if (!Array.isArray(ports) || ports.length === 0) return DASH
  const names = ports.map((p) => (p?.port ? String(p.port) : '')).filter(Boolean)
  return `<span class="label label-info" data-toggle="tooltip" data-placement="top" title="${escapeHtml(names.join(', '))}">${ports.length}</span>`
}

/** ProactiviteeServersModel (fara OdHostType - leftover). */
export function serverColumns(): Column[] {
  return [
    { index: 0, title: '', orderable: false, visible: true, width: 50, html: (r) => expandCell(r as DeviceRow) },
    { index: 1, title: 'Type', name: 'Tip', orderable: true, visible: true, width: 100, html: (r) => typeCell(r as DeviceRow) },
    { index: 2, title: 'Device name', name: 'Denumire', orderable: true, visible: true, html: (r) => nameCell(r as DeviceRow) },
    { index: 3, title: 'Role', orderable: false, visible: true, html: (r) => {
      const role = (r as DeviceRow).server_service?.role ?? (r as DeviceRow).server_role
      if (!role?.label) return DASH
      return `<span class="label label-primary" data-toggle="tooltip" data-placement="top" title="${escapeHtml('runs ' + (role.matched ?? ''))}">${escapeHtml(role.label)}</span>`
    } },
    { index: 4, title: 'Status', orderable: false, visible: true, html: (r) => statusBadge((r as DeviceRow).server_service) },
    { index: 5, title: 'Focus', orderable: false, visible: true, html: (r) => focusBadge(r as DeviceRow) },
    { index: 6, title: 'Services', orderable: false, visible: true, html: (r) => servicesCell(r as DeviceRow) },
    { index: 7, title: 'Critical alarms', name: 'CriticalAlarms', orderable: true, visible: true, html: (r) => alarmLabel((r as DeviceRow).CriticalAlarms, 'label-danger') },
    { index: 8, title: 'Major alarms', name: 'MajorAlarms', orderable: true, visible: true, html: (r) => alarmLabel((r as DeviceRow).MajorAlarms, 'label-warning') },
    { index: 9, title: 'Uptime', name: 'uptime_seconds', orderable: true, visible: true, html: (r) => uptimeCell((r as DeviceRow).uptime_seconds) },
    { index: 10, title: 'CPU usage', name: 'cpu_usage', orderable: true, visible: true, html: (r) => ((r as DeviceRow).cpu_usage ? gauge((r as DeviceRow).cpu_usage, 'cpu') : DASH) },
    { index: 11, title: 'Memory usage', name: 'ram_usage', orderable: true, visible: true, html: (r) => ((r as DeviceRow).ram_usage ? gauge((r as DeviceRow).ram_usage, 'ram') : DASH) },
    { index: 12, title: 'Swap usage', name: 'swap_usage', orderable: true, visible: true, html: (r) => {
      const v = (r as DeviceRow).swap_usage
      return v !== null && v !== '' && typeof v !== 'undefined' ? gauge(v, 'swap') : DASH
    } },
    { index: 13, title: 'Network connections', name: 'net_connections', orderable: true, visible: true, html: (r) => {
      const n = parseInt(String((r as DeviceRow).net_connections ?? 0), 10)
      return n > 0 ? `<span class="label label-info" data-od-live="net">${escapeHtml(n)}</span>` : DASH
    } },
    { index: 14, title: 'Open Ports', orderable: false, visible: true, html: (r) => openPortsCell(r as DeviceRow) },
    { index: 15, title: 'Running applications', name: 'running_applications', orderable: true, visible: true, html: (r) => ((r as DeviceRow).running_applications ? `<span class="label label-success">${escapeHtml((r as DeviceRow).running_applications)}</span>` : DASH) },
    { index: 16, title: 'Core Application', orderable: false, visible: true, html: (r) => coreApp(r as DeviceRow) },
    { index: 17, title: 'Last login', orderable: false, visible: true, html: (r) => lastLoginCell(r as DeviceRow) },
    { index: 18, title: 'Office', name: 'SediuID', orderable: true, visible: true, html: (r) => textOrDash((r as DeviceRow).office) },
    { index: 19, title: 'Actions', name: 'ID', orderable: false, visible: true, width: 115, html: (r) => ((r as DeviceRow).actions ? String((r as DeviceRow).actions) : DASH) },
    { index: 20, title: 'Pending Commands', name: 'commands_pending', orderable: true, visible: false, width: 100, html: (r) => pendingCommands(r as DeviceRow) },
  ]
}

/**
 * Extended Monitoring - ProactiviteeExtendedModel. Lista asta NU e lista
 * masinilor: are alte coloane (Group, Sessions, Resource consuming, Last
 * Activity) si nu are alarme, procesoare sau memorie. Se desena cu
 * `deviceColumns()`, adica omul vedea All Devices sub alt nume.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function ordinal(n: number): string {
  const b = n % 100
  if (b >= 11 && b <= 13) return `${n}th`
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`
}

/** moment(x).format('MMM Do, HH:mm') - titlul celulei Last Activity. */
export function shortStamp(when: Date): string {
  const two = (n: number) => String(n).padStart(2, '0')
  return `${MONTHS[when.getMonth()]} ${ordinal(when.getDate())}, ${two(when.getHours())}:${two(when.getMinutes())}`
}

/** Numele masinii cu proprietarul dedesubt, dar fara "Unassigned": modelul vechi tacea. */
function extendedNameCell(row: DeviceRow): string {
  const name = `<p>${escapeHtml(row.Denumire)}</p>`
  if (!row.Nume) return name
  return name + `<span class="color-light-grey"><i class="fa fa-user"></i> ${escapeHtml(row.Prenume)} ${escapeHtml(row.Nume)}</span>`
}

function lastActivityCell(row: DeviceRow): string {
  const when = stamp(row.DataAccesareDM)
  if (!when) return DASH
  return `<div title="${escapeHtml(shortStamp(when))}">${escapeHtml(fromNow(when))}</div>`
}

/** ProactiviteeExtendedModel (public/js/proactivitee.js). */
export function extendedColumns(): Column[] {
  return [
    { index: 0, title: '', orderable: false, visible: true, width: 50, html: (r) => expandCell(r as DeviceRow) },
    { index: 1, title: 'Type', name: 'Tip', orderable: true, visible: true, width: 100, html: (r) => `<center>${(r as DeviceRow).device_icon}</center>` },
    { index: 2, title: 'Device', name: 'Denumire', orderable: true, visible: true, html: (r) => extendedNameCell(r as DeviceRow) },
    { index: 3, title: 'Last User Logged On', name: 'UserLogat', orderable: true, visible: true, html: (r) => textOrDash((r as DeviceRow).UserLogat) },
    { index: 4, title: 'Office', name: 'SediuID', orderable: true, visible: true, html: (r) => textOrDash((r as DeviceRow).office) },
    { index: 5, title: 'Group', name: 'GroupID', orderable: true, visible: true, html: (r) => textOrDash((r as DeviceRow).groupName) },
    { index: 6, title: 'Sessions', name: 'sessions', orderable: true, visible: true, html: (r) => escapeHtml((r as DeviceRow).sessions ?? 0) },
    /* Resource consuming: vechiul SQL scria `0 as resources`, deci coloana a
       aratat mereu 0. Se pastreaza asa - nu se inventeaza o cifra care nu era. */
    { index: 7, title: 'Resource consuming', orderable: false, visible: true, html: (r) => escapeHtml((r as DeviceRow).resources ?? 0) },
    { index: 8, title: 'Last Activity', name: 'DataAccesareDM', orderable: true, visible: true, html: (r) => lastActivityCell(r as DeviceRow) },
  ]
}
